import { TrendingUp, Users, Trophy, Coins } from 'lucide-react';
import PlayerNameLink from '../../components/PlayerNameLink';
import {
    AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';

export const AnalyticsChartsAdminTab = ({
    revenueChart,
    playerActivity,
    topPlayers,
}) => (
    <div className="space-y-6" data-testid="analytics-charts-content">
        <div className="card-dark p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#39FF14]" />
                Revenue & Profit (Last 30 Days)
            </h3>
            {revenueChart.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={revenueChart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="date" stroke="#666" tick={{ fill: '#888', fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis stroke="#666" tick={{ fill: '#888', fontSize: 11 }} />
                        <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, color: '#fff' }} />
                        <Legend />
                        <Area type="monotone" dataKey="wagered" name="Wagered" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.15} />
                        <Area type="monotone" dataKey="won" name="Won" stroke="#FFD700" fill="#FFD700" fillOpacity={0.15} />
                        <Area type="monotone" dataKey="profit" name="Profit" stroke="#39FF14" fill="#39FF14" fillOpacity={0.15} />
                    </AreaChart>
                </ResponsiveContainer>
            ) : (
                <p className="text-[#A0AAB2] text-center py-8">No revenue data yet</p>
            )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card-dark p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#3B82F6]" />
                    Player Activity
                </h3>
                {playerActivity.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={playerActivity}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                            <XAxis dataKey="date" stroke="#666" tick={{ fill: '#888', fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                            <YAxis stroke="#666" tick={{ fill: '#888', fontSize: 11 }} />
                            <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, color: '#fff' }} />
                            <Legend />
                            <Bar dataKey="new_users" name="New Users" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="active_users" name="Active Users" fill="#A855F7" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <p className="text-[#A0AAB2] text-center py-8">No activity data yet</p>
                )}
            </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card-dark p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[#FFD700]" />
                    Top Winners
                </h3>
                <Table>
                    <TableHeader>
                        <TableRow className="border-white/10">
                            <TableHead className="text-[#A0AAB2]">#</TableHead>
                            <TableHead className="text-[#A0AAB2]">Player</TableHead>
                            <TableHead className="text-[#A0AAB2] text-right">Total Won</TableHead>
                            <TableHead className="text-[#A0AAB2] text-right">Biggest</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(topPlayers.top_winners || []).map((p, i) => (
                            <TableRow key={i} className="border-white/5">
                                <TableCell className="text-white font-bold">{i + 1}</TableCell>
                                <TableCell className="text-white">
                                    <PlayerNameLink id={p.user_id} name={p.user_name} testId={`top-winner-${i}`} />
                                </TableCell>
                                <TableCell className="text-right text-[#39FF14] font-mono">{(p.total_won || 0).toFixed(0)}</TableCell>
                                <TableCell className="text-right text-[#FFD700] font-mono">{(p.biggest_win || 0).toFixed(0)}</TableCell>
                            </TableRow>
                        ))}
                        {(!topPlayers.top_winners || topPlayers.top_winners.length === 0) && (
                            <TableRow><TableCell colSpan={4} className="text-center text-[#A0AAB2]">No data</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="card-dark p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Coins className="w-5 h-5 text-[#3B82F6]" />
                    Top Wagerers
                </h3>
                <Table>
                    <TableHeader>
                        <TableRow className="border-white/10">
                            <TableHead className="text-[#A0AAB2]">#</TableHead>
                            <TableHead className="text-[#A0AAB2]">Player</TableHead>
                            <TableHead className="text-[#A0AAB2] text-right">Wagered</TableHead>
                            <TableHead className="text-[#A0AAB2] text-right">Net P/L</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(topPlayers.top_wagerers || []).map((p, i) => (
                            <TableRow key={i} className="border-white/5">
                                <TableCell className="text-white font-bold">{i + 1}</TableCell>
                                <TableCell className="text-white">{p.name}</TableCell>
                                <TableCell className="text-right text-white font-mono">{(p.total_wagered || 0).toFixed(0)}</TableCell>
                                <TableCell className={`text-right font-mono ${(p.net || 0) >= 0 ? 'text-[#39FF14]' : 'text-[#FF007F]'}`}>
                                    {(p.net || 0) >= 0 ? '+' : ''}{(p.net || 0).toFixed(0)}
                                </TableCell>
                            </TableRow>
                        ))}
                        {(!topPlayers.top_wagerers || topPlayers.top_wagerers.length === 0) && (
                            <TableRow><TableCell colSpan={4} className="text-center text-[#A0AAB2]">No data</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    </div>
);
